<template>
    <section class="container relative">
        <div
            class="rounded-20 bg-main-section px-10 pt-10 max-lg:pt-36 md:h-[calc(100dvh_-_60px)] lg:h-[calc(100dvh_-_100px)]">
            <div
                class="flex h-full items-center justify-center max-lg:hidden md:max-h-[calc(100dvh_-_140px)]">
                <div class="flex justify-center space-x-10">
                    <div class="flex w-full items-center">
                        <h2
                            class="whitespace-nowrap text-32/100 font-semibold md:pl-8 xl:text-48/100">
                            Ничего Cromi
                        </h2>
                    </div>
                    <FIrstScreen
                        :tabs="tabs"
                        :tab="activeTab"
                        class="lg:max-xl:my-auto" />
                    <div class="relative flex w-full flex-col pt-[12.5rem]">
                        <h2
                            class="absolute top-1/2 -translate-y-1/2 translate-x-4 text-32/100 font-semibold xl:text-48/100">
                            чистого звука
                        </h2>
                        <div class="lg:pt-32 xl:pl-5 xl:pt-16">
                            <p>
                                Радиогиды, аудиогиды, оборудование синхронного
                                перевода и Доступная среда. Производство,
                                продажа и аренда.
                            </p>
                            <AtomButton
                                class="mt-3"
                                @click="openFeedbackModal">
                                Сделать запрос
                            </AtomButton>
                        </div>
                    </div>
                </div>
            </div>
            <div class="relative z-30 -translate-y-full pb-4 max-lg:hidden">
                <div
                    v-if="route.name === 'index'"
                    class="flex justify-between space-x-5 xl:space-x-10">
                    <button
                        v-for="tab in tabs"
                        :key="tab.slug"
                        :aria-label="tab.title"
                        :class="
                            tab.slug === activeTab.slug
                                ? 'text-accent-100'
                                : 'text-accent-100/50'
                        "
                        class="text-14/148 font-semibold transition-colors duration-300 xl:text-16/148"
                        @click="activeTab = tab">
                        {{ tab.title }}
                    </button>
                </div>
            </div>

            <div
                class="flex h-full shrink-0 grow flex-col items-center justify-center pb-8 sm:pb-16 md:pt-16 lg:hidden">
                <AtomTitle
                    level="1"
                    class="text-center">
                    Ничего&nbsp;Cromi чистого&nbsp;звука
                </AtomTitle>
                <div
                    class="relative flex h-full w-full items-center justify-center">
                    <CromiIcon />
                    <NuxtLink
                        v-if="activeTab.to"
                        :to="activeTab.to"
                        :aria-label="activeModelLinkLabel"
                        class="absolute top-1/2 h-auto w-full max-w-[14rem] -translate-y-1/2">
                        <picture>
                            <source
                                type="image/webp"
                                :srcset="
                                    activeTab.mobile_image_webp ||
                                    activeTab.image_webp
                                " />

                            <img
                                width="549"
                                height="934"
                                loading="eager"
                                fetchpriority="high"
                                decoding="async"
                                :alt="activeTab.title"
                                :src="activeTab.image" />
                        </picture>
                    </NuxtLink>
                </div>
                <div
                    class="flex w-full max-w-xs flex-col items-center justify-center md:pt-6">
                    <p class="text-center text-16/148">
                        Аренда и продажа радиогидов, аудиогидов и оборудования
                        синхронного перевода.
                    </p>
                    <AtomButton
                        class="mt-3 w-fit"
                        @click="openFeedbackModal">
                        Сделать запрос
                    </AtomButton>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import AtomButton from '~/components/atom/atom-button/AtomButton.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import CromiIcon from '~/components/pages/home/main-section/svg/CromiIcon.vue'
import FIrstScreen from '~/components/particles/first-screen/FIrstScreen.vue'
import type { TabInfo } from '~/components/particles/first-screen/tab.types'

const tabs: TabInfo[] = [
    {
        slug: 'radioguides-tab',
        title: 'Радиогиды',
        to: '/radioguide/reinvox-duo-sale/',
        tooltip: 'Радиогид Reinvox DUO',
        directionSlug: 'radioguide',
        image: '/assets/images/products/radioguide/jpg-png/reinvox-duo-bright.png',
        image_webp:
            '/assets/images/products/radioguide/webp/reinvox-duo-bright.webp',
        mobile_image_webp:
            '/assets/images/products/radioguide/webp/reinvox-duo-bright-mobile.webp',
        circles: [
            { text: 'Чистый звук без шума' },
            { text: 'Радиогиды для экскурсий на новом уровне' },
            { text: 'Дальность до 200 м' },
            { text: 'Держит заряд дольше смартфонов' },
        ],
    },
    {
        slug: 'audioguides-tab',
        title: 'Аудиогиды',
        to: '/audioguide/a-1-sale/',
        tooltip: 'Аудиогид SPBAUDIO A-1',
        directionSlug: 'audioguide',
        image: '/assets/images/products/kit/jpg-png/audioguide-a1-a5.png',
        image_webp: '/assets/images/products/kit/webp/audioguide-a1-a5.webp',
        circles: [
            { text: 'Студийный голос диктора' },
            { text: 'Аудиогиды для автономных экскурсий' },
            { text: '140 часов аудио на 32 языках' },
            { text: 'Производство в Санкт-Петербурге' },
        ],
    },
    {
        slug: 'sync-translate-tab',
        title: 'Оборудование синхронного перевода',
        to: '/sinhroperevod/max-2-sale/',
        tooltip: 'Система синхроперевода с SPBAUDIO MAX-2',
        directionSlug: 'sinhroperevod',
        image: '/assets/images/products/sinhroperevod/jpg-png/sinhroperevod-scale.png',
        image_webp:
            '/assets/images/products/sinhroperevod/webp/sinhroperevod-scale.webp',
        circles: [
            { text: 'Чистый звук без шума' },
            { text: 'Синхронный перевод на нескольких языках' },
            { text: 'Приемники держат заряд дольше смартфонов' },
            { text: 'Производство пультов в Санкт-Петербурге' },
        ],
    },
    {
        slug: 'cabs-tab',
        title: 'Кабины переводчиков',
        to: '/cabin/eurocab-1-sale/',
        tooltip: 'Напольные кабины переводчиков EUROCAB-1',
        directionSlug: 'cabin',
        image: '/assets/images/products/cabin/jpg-png/eurocab-1.png',
        image_webp: '/assets/images/products/cabin/webp/eurocab-1.webp',
        circles: [
            { text: 'Звукоизоляция по ISO4043' },
            { text: 'Быстросборные кабины переводчиков' },
            { text: 'Каркас из авиационного анодированного алюминия' },
            { text: 'Производство в Санкт-Петербурге' },
        ],
    },
    {
        slug: 'conference-systems-tab',
        title: 'Конференц-системы',
        to: '/conference-system/reinvox-concept-sale/',
        tooltip: 'Конференц-системы Reinvox Concept',
        directionSlug: 'conference-system',
        image: '/assets/images/products/kit/jpg-png/reinvox-elegant-concept.png',
        image_webp:
            '/assets/images/products/kit/webp/reinvox-elegant-concept.webp',
        circles: [
            { text: 'Высокая разборчивость речи' },
            { text: 'Конференц-системы профессионального уровня' },
            { text: 'До 75 микрофонов в цепи' },
            { text: 'Запись на SD и USB' },
        ],
    },
    {
        slug: 'accessible-environment-tab',
        title: 'Доступная среда',
        to: '/dostupsreda/tiflo-sale/',
        tooltip: 'Оборудование для тифлокомментирования',
        directionSlug: 'dostupsreda',
        image: '/assets/images/products/available-environment/jpg-png/accessible-environment.png',
        image_webp:
            '/assets/images/products/available-environment/webp/accessible-environment.webp',
        circles: [
            { text: 'Громкий чистый звук с широкой регулировкой' },
            { text: 'Тифлокомментирование и радиоклассы' },
            { text: 'Дальность до 200 м' },
            { text: 'Легко управляется на ощупь' },
        ],
    },
]

const route = useRoute()
const modalStore = useModalStore()

const activeTab = ref(
    tabs.find((tab) => route.fullPath.includes(tab.directionSlug)) || tabs[0]
)
const activeModelLinkLabel = computed(() =>
    (activeTab.value.tooltip?.trim() || stripHtml(activeTab.value.title))
        .replaceAll('&nbsp;', ' ')
        .replace(/\s+/g, ' ')
        .trim()
)

useHead(() => ({
    link: [
        {
            key: `home-mobile-lcp:${activeTab.value.slug}`,
            rel: 'preload',
            as: 'image',
            href:
                activeTab.value.mobile_image_webp || activeTab.value.image_webp,
            type: 'image/webp',
            media: '(max-width: 1023px)',
            fetchpriority: 'high',
        },
    ],
}))

function openFeedbackModal() {
    modalStore.setFormName('Главный экран, "Сделать запрос"')
    modalStore.open('feedbackModal')
}
</script>
